import React from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableFooter,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  headerCell: {
    backgroundColor: theme.palette.type === "dark" ? "#37474f" : theme.palette.primary.main,
    color: "#ffffff",
    fontWeight: "bold",
    fontSize: "0.875rem",
    padding: theme.spacing(1),
    "&.MuiTableCell-stickyHeader": {
      backgroundColor: theme.palette.type === "dark" ? "#37474f" : theme.palette.primary.main,
      color: "#ffffff",
    },
  },
  cell: {
    fontSize: "0.875rem",
    padding: theme.spacing(1),
    verticalAlign: "middle",
  },
  groupCell: {
    fontSize: "0.875rem",
    padding: theme.spacing(1),
    verticalAlign: "middle",
    borderTop: `2px solid ${theme.palette.divider}`,
  },
  totalRow: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: "bold",
  },
  emptyText: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const typeLabel = (type) => {
  if (type === "move") return i18n.t("usageReport.forwarding.types.move");
  if (type === "copy") return i18n.t("usageReport.forwarding.types.copy");
  return i18n.t("usageReport.forwarding.types.create");
};

// Agrupa o array plano forwardedToOthers por usuário DESTINO (receiver), e dentro
// de cada destino por usuário ORIGEM (sender), preservando a lista de tipos.
// Ordenação destino-primária: receiverName, depois senderName, depois type.
const buildDestinationGroups = (forwardedToOthers) => {
  const byReceiver = new Map();

  const sorted = [...forwardedToOthers].sort(
    (a, b) =>
      (a.receiverName || "").localeCompare(b.receiverName || "") ||
      (a.senderName || "").localeCompare(b.senderName || "") ||
      (a.type || "").localeCompare(b.type || "")
  );

  for (const row of sorted) {
    const receiverKey = String(row.receiverId);
    if (!byReceiver.has(receiverKey)) {
      byReceiver.set(receiverKey, {
        receiverId: row.receiverId,
        receiverName: row.receiverName,
        senders: new Map(),
        totalRows: 0,
        totalCount: 0,
      });
    }
    const dest = byReceiver.get(receiverKey);

    const senderKey = String(row.senderId);
    if (!dest.senders.has(senderKey)) {
      dest.senders.set(senderKey, {
        senderId: row.senderId,
        senderName: row.senderName,
        items: [],
      });
    }
    dest.senders.get(senderKey).items.push({ type: row.type, count: row.count });
    dest.totalRows += 1;
    dest.totalCount += Number(row.count) || 0;
  }

  return Array.from(byReceiver.values());
};

const KanbanCardsReportView = ({
  reportData,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  const classes = useStyles();

  const createdStats = reportData?.createdStats || [];
  const forwardedToOthers = reportData?.forwardedToOthers || [];

  const destinationGroups = buildDestinationGroups(forwardedToOthers);
  const grandTotal = forwardedToOthers.reduce(
    (acc, r) => acc + (Number(r.count) || 0),
    0
  );

  const pageGroups = destinationGroups.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Seção: Criação de Cards */}
      <Typography variant="subtitle1" className={classes.sectionTitle}>
        {i18n.t("usageReport.forwarding.createdTitle")}
      </Typography>
      {createdStats.length === 0 ? (
        <Typography variant="body2" className={classes.emptyText}>
          {i18n.t("usageReport.forwarding.createdEmpty")}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>{i18n.t("usageReport.columns.user")}</TableCell>
                <TableCell className={classes.headerCell} align="center">
                  {i18n.t("usageReport.forwarding.cardsCreated")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {createdStats.map((row) => (
                <TableRow key={row.userId} hover>
                  <TableCell className={classes.cell}>{row.userName}</TableCell>
                  <TableCell className={classes.cell} align="center">
                    {row.cardsCreated}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className={classes.totalRow}>
                <TableCell className={classes.cell}>
                  <strong>{i18n.t("common.total")}</strong>
                </TableCell>
                <TableCell className={classes.cell} align="center">
                  <strong>
                    {createdStats.reduce((acc, r) => acc + r.cardsCreated, 0)}
                  </strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Seção: Cards Encaminhados a Outros Usuários (paginada por destino) */}
      <Typography variant="subtitle1" className={classes.sectionTitle}>
        {i18n.t("usageReport.forwarding.forwardedTitle")}
      </Typography>
      {forwardedToOthers.length === 0 ? (
        <Typography variant="body2" className={classes.emptyText}>
          {i18n.t("usageReport.forwarding.forwardedEmpty")}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>{i18n.t("usageReport.forwarding.origin")}</TableCell>
                <TableCell className={classes.headerCell}>{i18n.t("usageReport.forwarding.destination")}</TableCell>
                <TableCell className={classes.headerCell}>{i18n.t("common.type")}</TableCell>
                <TableCell className={classes.headerCell} align="center">
                  {i18n.t("usageReport.forwarding.quantity")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageGroups.map((dest, destIdx) => {
                const senders = Array.from(dest.senders.values());
                let destCellRendered = false;
                return senders.map((sender, senderIdx) =>
                  sender.items.map((item, itemIdx) => {
                    const isFirstRowOfDest =
                      senderIdx === 0 && itemIdx === 0;
                    const isFirstRowOfSender = itemIdx === 0;
                    // Borda superior separando blocos de destino (exceto o 1º da página)
                    const groupBorder =
                      isFirstRowOfDest && destIdx !== 0;
                    const cellCls = groupBorder ? classes.groupCell : classes.cell;

                    const row = (
                      <TableRow
                        key={`${dest.receiverId}-${sender.senderId}-${itemIdx}`}
                        hover
                      >
                        {isFirstRowOfSender && (
                          <TableCell
                            className={cellCls}
                            rowSpan={sender.items.length}
                          >
                            {sender.senderName}
                          </TableCell>
                        )}
                        {isFirstRowOfDest && !destCellRendered && (
                          <TableCell
                            className={cellCls}
                            rowSpan={dest.totalRows}
                          >
                            {dest.receiverName}
                          </TableCell>
                        )}
                        <TableCell className={cellCls}>
                          {typeLabel(item.type)}
                        </TableCell>
                        <TableCell className={cellCls} align="center">
                          {item.count}
                        </TableCell>
                      </TableRow>
                    );
                    if (isFirstRowOfDest) destCellRendered = true;
                    return row;
                  })
                );
              })}
              <TableRow className={classes.totalRow}>
                <TableCell className={classes.cell} colSpan={3}>
                  <strong>{i18n.t("usageReport.forwarding.totalAllDestinations")}</strong>
                </TableCell>
                <TableCell className={classes.cell} align="center">
                  <strong>{grandTotal}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  count={destinationGroups.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={onChangePage}
                  onChangeRowsPerPage={onChangeRowsPerPage}
                  labelRowsPerPage={i18n.t("usageReport.forwarding.rowsPerPage")}
                  labelDisplayedRows={({ from, to, count }) =>
                    i18n.t("usageReport.forwarding.displayedRows", { from, to, total: count })
                  }
                  colSpan={4}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default KanbanCardsReportView;
